import React, { useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Alert, Box, Button, CircularProgress, IconButton, Paper, Stack, Typography } from "@mui/material"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import CloseIcon from "@mui/icons-material/Close"
import FileOpenIcon from "@mui/icons-material/FileOpen"
import FolderZipIcon from "@mui/icons-material/FolderZip"

import type {
  CsvOrderRow,
  MeeshoPaymentFile,
  SkuCostConfig
} from "@/features/profit-calculators/calculator-shared/types"
import { useMonthlyReports } from "@/features/profit-calculators/calculator-shared/hooks/useMonthlyReports"
import {
  validateMeeshoMonth,
  validateMeeshoPaymentMonth
} from "@/features/profit-calculators/meesho-calculator/utils/monthValidation"
import {
  computeCombinedReport,
  parsePaymentFile,
  parsePaymentZip
} from "@/features/profit-calculators/meesho-calculator/utils/parsePaymentFile"
import {
  buildInitialSkuCosts,
  CalculatorWizardHeader,
  loadSkuCosts,
  monthKeyToLabel,
  persistSkuCosts,
  SkuCostsStep,
  type UniqueSku
} from "@/features/profit-calculators/calculator-shared/components/CalculatorWizardShared"
import SkuCostModal from "@/features/profit-calculators/calculator-shared/components/SkuCostModal"
import WizardDropZone from "@/features/profit-calculators/calculator-shared/components/WizardDropZone"
import { getReportMonthGenerationError } from "@/features/profit-calculators/calculator-shared/utils/reportMonthUtils"

const STEPS = ["Upload Orders CSV", "Configure SKU Costs", "Payment ZIP 1", "Payment ZIP 2", "Generate Report"]
const SKU_STORAGE_KEY = "meesho_sku_costs"

const extractUniqueSkus = (orders: CsvOrderRow[]): UniqueSku[] => {
  const skus = new Map<string, string>()
  orders.forEach(order => skus.set(order.sku || "UNKNOWN", order.productName || "Unknown Product"))
  return Array.from(skus, ([sku, productName]) => ({ sku, productName }))
}

const MeeshoCalculatorPage: React.FC = () => {
  const { monthKey = "" } = useParams<{ monthKey: string }>()
  const navigate = useNavigate()
  const targetMonthLabel = useMemo(() => monthKeyToLabel(monthKey), [monthKey])
  const reportMonthError = useMemo(() => getReportMonthGenerationError(monthKey), [monthKey])
  const reportYear = Number(monthKey.slice(0, 4)) || new Date().getFullYear()
  const nextMonthLabel = useMemo(() => {
    const [year, month] = monthKey.split("-").map(Number)
    const nextMonth = month === 12 ? 1 : month + 1
    const nextYear = month === 12 ? year + 1 : year
    return monthKeyToLabel(`${nextYear}-${String(nextMonth).padStart(2, "0")}`)
  }, [monthKey])
  const { saveReport, reportExists } = useMonthlyReports("meesho", reportYear)

  const [activeStep, setActiveStep] = useState(0)
  const [ordersFileName, setOrdersFileName] = useState("")
  const [ordersSourceFile, setOrdersSourceFile] = useState<File | null>(null)
  const [orders, setOrders] = useState<CsvOrderRow[]>([])
  const [uniqueSkus, setUniqueSkus] = useState<UniqueSku[]>([])
  const [skuCosts, setSkuCosts] = useState<Record<string, SkuCostConfig>>(() => loadSkuCosts(SKU_STORAGE_KEY))
  const [costsConfigured, setCostsConfigured] = useState(false)
  const [skuModalOpen, setSkuModalOpen] = useState(false)
  const [paymentFile1Name, setPaymentFile1Name] = useState("")
  const [paymentSourceFile1, setPaymentSourceFile1] = useState<File | null>(null)
  const [paymentFile1, setPaymentFile1] = useState<MeeshoPaymentFile | null>(null)
  const [paymentFile2Name, setPaymentFile2Name] = useState("")
  const [paymentSourceFile2, setPaymentSourceFile2] = useState<File | null>(null)
  const [paymentFile2, setPaymentFile2] = useState<MeeshoPaymentFile | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [generating, setGenerating] = useState(false)
  const [generateError, setGenerateError] = useState<string | null>(null)

  const monthReports = useMemo(
    () =>
      orders.length > 0 && paymentFile1 && paymentFile2
        ? computeCombinedReport(orders, skuCosts, [paymentFile1, paymentFile2])
        : null,
    [orders, skuCosts, paymentFile1, paymentFile2]
  )

  const persistReport = async () => {
    setGenerateError(null)
    if (!monthReports || !ordersSourceFile || !paymentSourceFile1 || !paymentSourceFile2) {
      setGenerateError("The uploaded source files are no longer available. Please upload them again.")
      return
    }

    const firstPaymentValidation = validateMeeshoPaymentMonth(paymentFile1!, targetMonthLabel)
    if (!firstPaymentValidation.valid) {
      setGenerateError(`Payment ZIP 1 is invalid. ${firstPaymentValidation.reason}`)
      return
    }

    const secondPaymentValidation = validateMeeshoPaymentMonth(paymentFile2!, nextMonthLabel)
    if (!secondPaymentValidation.valid) {
      setGenerateError(`Payment ZIP 2 is invalid. ${secondPaymentValidation.reason}`)
      return
    }

    const validation = validateMeeshoMonth(
      orders.map(order => order.orderDate || ""),
      targetMonthLabel
    )
    if (!validation.valid) {
      setGenerateError(validation.reason || "This upload does not match the selected month.")
      return
    }

    const report = monthReports[targetMonthLabel]
    if (!report) {
      setGenerateError(
        `No ${targetMonthLabel} data was found in the uploaded files. Please check the files and try again.`
      )
      return
    }

    setGenerating(true)
    try {
      await saveReport(monthKey, report, [
        { role: "orders", file: ordersSourceFile },
        { role: "payment-current-month", file: paymentSourceFile1 },
        { role: "payment-next-month", file: paymentSourceFile2 }
      ])
      navigate(`/meesho-calculator/${monthKey}/report`)
    } catch (err) {
      setGenerateError(err instanceof Error ? err.message : "Failed to save the report. Please try again.")
    } finally {
      setGenerating(false)
    }
  }

  const handleGenerateReport = () => {
    setGenerateError(null)
    if (reportMonthError) {
      setGenerateError(reportMonthError)
      return
    }
    if (reportExists(monthKey)) {
      setGenerateError("This report has already been generated and cannot be updated or deleted.")
      return
    }
    persistReport()
  }

  const handleOrdersLoaded = async (file: File) => {
    setLoading(true)
    setError(null)
    try {
      if (!file.name.toLowerCase().endsWith(".csv")) {
        throw new Error("Invalid file format. Please upload a .csv file.")
      }
      const result = await parsePaymentFile(file)
      if (result.csvOrders.length === 0) {
        throw new Error("No orders found in the CSV. Please check if the file format is correct.")
      }

      const validation = validateMeeshoMonth(
        result.csvOrders.map(order => order.orderDate || ""),
        targetMonthLabel
      )
      if (!validation.valid) throw new Error(validation.reason || `Please upload ${targetMonthLabel} files.`)

      const extractedSkus = extractUniqueSkus(result.csvOrders)
      setOrdersFileName(file.name)
      setOrdersSourceFile(file)
      setOrders(result.csvOrders)
      setUniqueSkus(extractedSkus)
      setSkuCosts(buildInitialSkuCosts(extractedSkus, SKU_STORAGE_KEY))
      setActiveStep(1)
      setSkuModalOpen(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to parse file.")
    } finally {
      setLoading(false)
    }
  }

  const handlePaymentFileLoaded = async (file: File, fileNumber: 1 | 2) => {
    setLoading(true)
    setError(null)
    try {
      if (!file.name.toLowerCase().endsWith(".zip")) {
        throw new Error("Invalid file format. Please upload a .zip file.")
      }
      const result = await parsePaymentZip(file)
      if (result.orders.length === 0) {
        throw new Error(`The spreadsheet inside "${file.name}" has no Order Payment transaction records.`)
      }

      const requiredMonth = fileNumber === 1 ? targetMonthLabel : nextMonthLabel
      const validation = validateMeeshoPaymentMonth(result, requiredMonth)
      if (!validation.valid) {
        throw new Error(`Payment ZIP ${fileNumber} is invalid. ${validation.reason}`)
      }

      if (fileNumber === 1) {
        setPaymentFile1Name(file.name)
        setPaymentSourceFile1(file)
        setPaymentFile1(result)
      } else {
        setPaymentFile2Name(file.name)
        setPaymentSourceFile2(file)
        setPaymentFile2(result)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to parse ZIP file.")
    } finally {
      setLoading(false)
    }
  }

  const handleSaveCosts = (costs: Record<string, SkuCostConfig>) => {
    setSkuCosts(costs)
    setCostsConfigured(true)
    persistSkuCosts(SKU_STORAGE_KEY, costs)
  }

  const handleRemoveOrders = () => {
    setOrdersFileName("")
    setOrdersSourceFile(null)
    setOrders([])
    setUniqueSkus([])
    setSkuCosts({})
    setCostsConfigured(false)
    setActiveStep(0)
  }

  const renderLoadedPaymentFile = (
    fileNumber: 1 | 2,
    fileName: string,
    paymentFile: MeeshoPaymentFile,
    onRemove: () => void
  ) => (
    <Paper
      variant="outlined"
      sx={{
        p: 3,
        borderRadius: 3,
        bgcolor: "rgba(34, 197, 94, 0.04)",
        borderColor: "success.main"
      }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={2} alignItems="center">
          <CheckCircleIcon sx={{ color: "success.main", fontSize: 28 }} />
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              Payment ZIP {fileNumber} Loaded Successfully
            </Typography>
            <Typography variant="caption" color="text.secondary">
              File: <code>{fileName}</code> | Period: <strong>{paymentFile.periodLabel}</strong>
              {fileNumber === 2 && ` | ${paymentFile.orders.length} order transactions`}
            </Typography>
          </Box>
        </Stack>
        <IconButton onClick={onRemove} color="error" title={`Remove Payment ZIP ${fileNumber}`}>
          <CloseIcon />
        </IconButton>
      </Stack>
    </Paper>
  )

  if (reportMonthError) {
    return (
      <Stack spacing={2} sx={{ pb: 6 }}>
        <Alert severity="error">{reportMonthError}</Alert>
        <Box>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate("/meesho-calculator")}>
            Back to Meesho Reports
          </Button>
        </Box>
      </Stack>
    )
  }

  return (
    <Box sx={{ pb: 6 }}>
      <CalculatorWizardHeader
        activeStep={activeStep}
        finalStep={4}
        steps={STEPS}
        platformName="Meesho"
        error={error}
        generateError={generateError}
        generating={generating}
        onClearError={() => setError(null)}
        onClearGenerateError={() => setGenerateError(null)}
        onRetryGenerate={handleGenerateReport}
      />

      {activeStep === 0 && (
        <Stack spacing={3}>
          <Typography variant="h6" fontWeight={750}>
            Step 1: Upload Meesho Orders CSV
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Export orders from: <strong>Meesho Supplier Panel → Orders → Download Orders CSV</strong>. This contains SKU
            details.
          </Typography>
          <WizardDropZone
            accept=".csv"
            title={`Upload Meesho Orders CSV for month of ${targetMonthLabel}`}
            subtitle={`Drag & drop or click to browse (only .csv accepted) • Selected month: ${targetMonthLabel}`}
            loading={loading}
            onFileSelected={handleOrdersLoaded}
            icon={<FileOpenIcon sx={{ fontSize: 40, color: "primary.main" }} />}
          />
        </Stack>
      )}

      {activeStep === 1 && (
        <SkuCostsStep
          fileName={ordersFileName}
          orderCount={orders.length}
          uniqueSkuCount={uniqueSkus.length}
          costsConfigured={costsConfigured}
          nextStepLabel="Payment ZIP 1"
          onConfigure={() => setSkuModalOpen(true)}
          onRemoveFile={handleRemoveOrders}
          onBack={() => setActiveStep(0)}
          onNext={() => setActiveStep(2)}
        />
      )}

      {activeStep === 2 && (
        <Stack spacing={3}>
          <Typography variant="h6" fontWeight={750}>
            Step 3: Upload Payment File 1 (ZIP Format)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            To calculate profit for {targetMonthLabel}, upload two payment ZIP files based on the orders CSV from Step
            1: one for <strong>{targetMonthLabel}</strong> and one for <strong>{nextMonthLabel}</strong>. Download them
            from: <strong>Payments → Previous Payments → Download Report</strong>.
          </Typography>
          {paymentFile1 ? (
            renderLoadedPaymentFile(1, paymentFile1Name, paymentFile1, () => {
              setPaymentFile1Name("")
              setPaymentSourceFile1(null)
              setPaymentFile1(null)
            })
          ) : (
            <WizardDropZone
              accept=".zip"
              title={`Upload Payment ZIP file for ${targetMonthLabel}`}
              subtitle={`Two zip files are required to calculate your ${targetMonthLabel} profit: one for ${targetMonthLabel} and one for ${nextMonthLabel} • Only .zip files are accepted`}
              loading={loading}
              onFileSelected={file => handlePaymentFileLoaded(file, 1)}
              icon={<FolderZipIcon sx={{ fontSize: 40, color: "warning.main" }} />}
            />
          )}
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => setActiveStep(1)}
              sx={{ borderRadius: 2 }}>
              Back
            </Button>
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              disabled={!paymentFile1}
              onClick={() => setActiveStep(3)}
              sx={{ borderRadius: 2 }}>
              Proceed to Payment ZIP 2
            </Button>
          </Stack>
        </Stack>
      )}

      {activeStep === 3 && (
        <Stack spacing={3}>
          <Typography variant="h6" fontWeight={750}>
            Step 4: Upload Payment File 2 (ZIP Format)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Upload the second required payment ZIP for <strong>{nextMonthLabel}</strong>. Together, the{" "}
            <strong>{targetMonthLabel}</strong> and <strong>{nextMonthLabel}</strong> files correspond to the orders CSV
            uploaded in Step 1.
          </Typography>
          {paymentFile2 ? (
            renderLoadedPaymentFile(2, paymentFile2Name, paymentFile2, () => {
              setPaymentFile2Name("")
              setPaymentSourceFile2(null)
              setPaymentFile2(null)
            })
          ) : (
            <WizardDropZone
              accept=".zip"
              title={`Upload Payment ZIP 2 of 2 for ${nextMonthLabel}`}
              subtitle={`Two zip files are required to calculate your ${targetMonthLabel} profit: one for ${targetMonthLabel} and one for ${nextMonthLabel} • Only .zip files are accepted`}
              loading={loading}
              onFileSelected={file => handlePaymentFileLoaded(file, 2)}
              icon={<FolderZipIcon sx={{ fontSize: 40, color: "warning.main" }} />}
            />
          )}
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => setActiveStep(2)}
              sx={{ borderRadius: 2 }}>
              Back
            </Button>
            <Button
              variant="contained"
              disabled={!paymentFile2 || generating || reportExists(monthKey)}
              onClick={handleGenerateReport}
              color="success"
              sx={{ borderRadius: 2, px: 3, fontWeight: 700 }}>
              {generating ? (
                <CircularProgress size={20} sx={{ color: "white" }} />
              ) : reportExists(monthKey) ? (
                "Report Already Generated"
              ) : (
                "Generate Profit Report"
              )}
            </Button>
          </Stack>
        </Stack>
      )}

      {skuModalOpen && (
        <SkuCostModal
          open={skuModalOpen}
          onClose={() => setSkuModalOpen(false)}
          uniqueSkus={uniqueSkus}
          initialCosts={skuCosts}
          onSave={handleSaveCosts}
        />
      )}
    </Box>
  )
}

export default MeeshoCalculatorPage
